import React, { useState, useEffect } from 'react';
import {
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
  Checkbox,
  FormControlLabel,
  Divider,
  Chip,
  FormGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  InputAdornment,
  Grid,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  Paper
} from '@mui/material';
import {
  Download,
  Description,
  TableChart,
  Code,
  Close,
  ExpandMore,
  Search,
  SelectAll,
  Deselect,
  Category,
  FilterList,
  DataUsage
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

// Helper functions directly in the component for now
const formatFieldName = fieldName => {
  return fieldName
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .replace(/([a-z])([A-Z])/g, '$1 $2');
};

const getFieldCategories = fields => {
  const categories = {
    'Shipment Info': [],
    'Origin Info': [],
    'Destination Info': [],
    'Carrier Info': [],
    'Financial Info': [],
    Dates: [],
    Other: []
  };

  fields.forEach(field => {
    const lowerField = field.toLowerCase();

    if (lowerField.includes('origin') || lowerField.includes('pickup')) {
      categories['Origin Info'].push(field);
    } else if (
      lowerField.includes('destination') ||
      lowerField.includes('delivery')
    ) {
      categories['Destination Info'].push(field);
    } else if (
      lowerField.includes('carrier') ||
      lowerField.includes('scac') ||
      lowerField.includes('pro')
    ) {
      categories['Carrier Info'].push(field);
    } else if (
      lowerField.includes('charge') ||
      lowerField.includes('cost') ||
      lowerField.includes('rate') ||
      lowerField.includes('net') ||
      lowerField.includes('surcharge') ||
      lowerField.includes('freight')
    ) {
      categories['Financial Info'].push(field);
    } else if (lowerField.includes('date') || lowerField.includes('time')) {
      categories['Dates'].push(field);
    } else if (
      lowerField.includes('id') ||
      lowerField.includes('number') ||
      lowerField.includes('status') ||
      lowerField.includes('type') ||
      lowerField.includes('rule')
    ) {
      categories['Shipment Info'].push(field);
    } else {
      categories['Other'].push(field);
    }
  });

  // Remove empty categories
  Object.keys(categories).forEach(key => {
    if (categories[key].length === 0) {
      delete categories[key];
    }
  });

  return categories;
};

// Export functions
const exportToCSV = (data, filename) => {
  if (!data || data.length === 0) {
    alert('No data to export');
    return;
  }

  // Get headers from first object
  const headers = Object.keys(data[0]);

  // Create CSV content
  const csvContent = [
    headers.join(','),
    ...data.map(row =>
      headers
        .map(header => {
          const value = row[header] || '';
          // Handle values with commas
          if (
            typeof value === 'string' &&
            (value.includes(',') || value.includes('"'))
          ) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        })
        .join(',')
    )
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  saveAs(blob, `${filename}_${new Date().toISOString().split('T')[0]}.csv`);
};

const exportToExcel = (data, filename, sheetName) => {
  if (!data || data.length === 0) {
    alert('No data to export');
    return;
  }

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(data);

  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(
    wb,
    `${filename}_${new Date().toISOString().split('T')[0]}.xlsx`
  );
};

const exportToJSON = (data, filename) => {
  if (!data || data.length === 0) {
    alert('No data to export');
    return;
  }

  const jsonString = JSON.stringify(data, null, 2);
  const blob = new Blob([jsonString], { type: 'application/json' });
  saveAs(blob, `${filename}_${new Date().toISOString().split('T')[0]}.json`);
};

const ExportButton = ({
  data = [],
  allShipments = [],
  filename = 'export',
  sheetName = 'Sheet1',
  onExport,
  size = 'small',
  variant = 'outlined',
  fullWidth = false,
  showIcon = true,
  label = 'Export'
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = useState(null);
  const [exportDialog, setExportDialog] = useState(false);
  const [exportFormat, setExportFormat] = useState('csv');
  const [exportSource, setExportSource] = useState('filtered'); // 'filtered' or 'raw'
  const [selectedFields, setSelectedFields] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [fieldCategories, setFieldCategories] = useState({});
  const [expandedCategories, setExpandedCategories] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // Check if we have both types of data
  const hasFilteredData = data.length > 0;
  const hasRawData = allShipments.length > 0;

  // Get all available fields from the full dataset
  useEffect(() => {
    if (hasRawData) {
      const fields = getAllFields(allShipments);
      const categories = getFieldCategories(fields);
      setFieldCategories(categories);
      setExpandedCategories(Object.keys(categories));

      // By default, select all fields
      setSelectedFields(fields);
      setSelectAll(true);
    }
  }, [allShipments, hasRawData]);

  const getAllFields = shipments => {
    if (!shipments || shipments.length === 0) return [];

    const allKeys = new Set();
    shipments.forEach(shipment => {
      Object.keys(shipment).forEach(key => {
        // Include most fields but exclude internal ones
        if (
          !key.startsWith('_') &&
          !key.toLowerCase().includes('password') &&
          !key.toLowerCase().includes('token')
        ) {
          allKeys.add(key);
        }
      });
    });

    return Array.from(allKeys).sort();
  };

  const handleClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleExport = format => {
    setExportFormat(format);
    setExportDialog(true);
    handleClose();
  };

  const handleExportConfirm = () => {
    let exportData;

    if (exportSource === 'filtered' && hasFilteredData) {
      // Use pre-filtered data directly
      exportData = data;
    } else {
      // Use raw data with field selection
      const sourceData = allShipments;

      if (sourceData.length === 0) {
        alert('No data to export');
        setExportDialog(false);
        return;
      }

      if (selectedFields.length === 0) {
        // If no fields selected, use all fields
        exportData = sourceData.map(item => {
          const formattedItem = {};
          Object.keys(item).forEach(key => {
            if (key && !key.startsWith('_')) {
              formattedItem[formatFieldName(key)] = formatValue(key, item[key]);
            }
          });
          return formattedItem;
        });
      } else {
        // Use only selected fields
        exportData = sourceData.map(item => {
          const formattedItem = {};
          selectedFields.forEach(field => {
            if (item.hasOwnProperty(field)) {
              formattedItem[formatFieldName(field)] = formatValue(
                field,
                item[field]
              );
            }
          });
          return formattedItem;
        });
      }
    }

    // Call export function
    if (onExport) {
      onExport(exportData, exportFormat, filename);
    } else {
      switch (exportFormat) {
        case 'csv':
          exportToCSV(exportData, filename);
          break;
        case 'excel':
          exportToExcel(exportData, filename, sheetName);
          break;
        case 'json':
          exportToJSON(exportData, filename);
          break;
        default:
          break;
      }
    }

    setExportDialog(false);
  };

  // Helper function to format values
  const formatValue = (field, value) => {
    if (value === null || value === undefined) return '';

    // Format dates
    if (field.toLowerCase().includes('date') && value) {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString();
        }
      } catch (e) {}
    }

    // Format currency
    if (
      (field.toLowerCase().includes('charge') ||
        field.toLowerCase().includes('cost') ||
        field.toLowerCase().includes('rate') ||
        field.toLowerCase().includes('freight') ||
        field.toLowerCase().includes('net')) &&
      typeof value === 'number'
    ) {
      return `$${value.toFixed(2)}`;
    }

    // Format numbers
    if (typeof value === 'number') {
      return value.toString();
    }

    // Return as is
    return value;
  };

  const handleFieldToggle = field => {
    setSelectedFields(prev => {
      const newSelection = prev.includes(field)
        ? prev.filter(f => f !== field)
        : [...prev, field];

      // Update selectAll state
      const allFields = Object.values(fieldCategories).flat();
      setSelectAll(newSelection.length === allFields.length);

      return newSelection;
    });
  };

  const handleCategoryToggle = (categoryFields, select) => {
    setSelectedFields(prev => {
      let newSelection;
      if (select) {
        // Add all fields from this category
        newSelection = [...new Set([...prev, ...categoryFields])];
      } else {
        // Remove all fields from this category
        newSelection = prev.filter(f => !categoryFields.includes(f));
      }

      // Update selectAll state
      const allFields = Object.values(fieldCategories).flat();
      setSelectAll(newSelection.length === allFields.length);

      return newSelection;
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedFields([]);
    } else {
      const allFields = Object.values(fieldCategories).flat();
      setSelectedFields(allFields);
    }
    setSelectAll(!selectAll);
  };

  const handleCategoryExpand = category => {
    setExpandedCategories(prev =>
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const filteredCategories = Object.entries(fieldCategories).reduce(
    (acc, [category, fields]) => {
      const filteredFields = fields.filter(field =>
        formatFieldName(field).toLowerCase().includes(searchTerm.toLowerCase())
      );
      if (filteredFields.length > 0) {
        acc[category] = filteredFields;
      }
      return acc;
    },
    {}
  );

  const buttonSize = isMobile ? 'small' : size;
  const totalSelected = selectedFields.length;
  const totalFields = Object.values(fieldCategories).flat().length;

  return (
    <>
      <Button
        variant={variant}
        size={buttonSize}
        startIcon={showIcon && <Download />}
        onClick={handleClick}
        fullWidth={fullWidth}
        sx={{
          borderColor: '#ddd',
          color: '#555',
          fontSize: isMobile ? '0.7rem' : '0.75rem',
          whiteSpace: 'nowrap',
          minWidth: isMobile ? 'auto' : 90,
          '&:hover': {
            borderColor: '#999',
            backgroundColor: '#f5f5f5'
          }
        }}
      >
        {isMobile && !showIcon ? 'EXP' : label}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <MenuItem onClick={() => handleExport('csv')}>
          <ListItemIcon>
            <Description fontSize='small' />
          </ListItemIcon>
          <ListItemText primary='Export as CSV' />
        </MenuItem>
        <MenuItem onClick={() => handleExport('excel')}>
          <ListItemIcon>
            <TableChart fontSize='small' />
          </ListItemIcon>
          <ListItemText primary='Export as Excel' />
        </MenuItem>
        {/* for Dev debug purpose */}
        {/* <MenuItem onClick={() => handleExport('json')}>
          <ListItemIcon>
            <Code fontSize='small' />
          </ListItemIcon>
          <ListItemText primary='Export as JSON' />
        </MenuItem> */}
      </Menu>

      <Dialog
        open={exportDialog}
        onClose={() => setExportDialog(false)}
        maxWidth='md'
        fullWidth
        PaperProps={{
          sx: { minHeight: '60vh', maxHeight: '80vh' }
        }}
      >
        <DialogTitle>
          <Box
            display='flex'
            alignItems='center'
            justifyContent='space-between'
          >
            <Typography variant='h6'>Export Configuration</Typography>
            <IconButton size='small' onClick={() => setExportDialog(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <Box sx={{ mb: 3 }}>
            <Typography variant='subtitle2' gutterBottom>
              Export Format: {exportFormat.toUpperCase()}
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Data Source Selection */}
          {hasFilteredData && hasRawData && (
            <Box sx={{ mb: 3 }}>
              <FormControl component='fieldset'>
                <FormLabel component='legend'>Select Data to Export</FormLabel>
                <RadioGroup
                  row
                  value={exportSource}
                  onChange={e => setExportSource(e.target.value)}
                >
                  <FormControlLabel
                    value='filtered'
                    control={<Radio size='small' />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <FilterList sx={{ fontSize: '1rem', mr: 0.5 }} />
                        <Typography variant='body2'>
                          Filtered Data ({data.length} records)
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value='raw'
                    control={<Radio size='small' />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <DataUsage sx={{ fontSize: '1rem', mr: 0.5 }} />
                        <Typography variant='body2'>
                          All Raw Data ({allShipments.length} records)
                        </Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
              </FormControl>
            </Box>
          )}

          {/* Show different UI based on selected source */}
          {exportSource === 'filtered' && hasFilteredData ? (
            <Box
              sx={{
                p: 2,
                textAlign: 'center',
                bgcolor: '#f5f5f5',
                borderRadius: 1
              }}
            >
              <Typography variant='body2' color='textSecondary'>
                Exporting filtered data with current view format.
              </Typography>
              <Typography
                variant='caption'
                color='textSecondary'
                sx={{ display: 'block', mt: 1 }}
              >
                Total records: {data.length}
              </Typography>
            </Box>
          ) : (
            /* Raw Data Field Selection UI */
            hasRawData && (
              <Box sx={{ mb: 2 }}>
                <Box
                  display='flex'
                  alignItems='center'
                  justifyContent='space-between'
                  mb={1}
                >
                  <Typography variant='subtitle2'>
                    Select Fields to Export ({totalSelected}/{totalFields}{' '}
                    selected)
                  </Typography>
                  <Box>
                    <Button
                      size='small'
                      onClick={handleSelectAll}
                      startIcon={selectAll ? <Deselect /> : <SelectAll />}
                      sx={{ mr: 1 }}
                    >
                      {selectAll ? 'Deselect All' : 'Select All'}
                    </Button>
                  </Box>
                </Box>

                <TextField
                  fullWidth
                  size='small'
                  placeholder='Search fields...'
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start'>
                        <Search fontSize='small' />
                      </InputAdornment>
                    )
                  }}
                  sx={{ mb: 2 }}
                />

                <Box sx={{ maxHeight: 400, overflow: 'auto', pr: 1 }}>
                  {Object.entries(filteredCategories).map(
                    ([category, fields]) => (
                      <Accordion
                        key={category}
                        expanded={expandedCategories.includes(category)}
                        onChange={() => handleCategoryExpand(category)}
                        sx={{ mb: 1 }}
                      >
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Box display='flex' alignItems='center' width='100%'>
                            <Category
                              sx={{
                                mr: 1,
                                fontSize: '1rem',
                                color: 'primary.main'
                              }}
                            />
                            <Typography variant='subtitle2'>
                              {category}
                            </Typography>
                            <Chip
                              size='small'
                              label={`${fields.length} fields`}
                              sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                            />
                            <Box flex={1} />
                            <Button
                              size='small'
                              onClick={e => {
                                e.stopPropagation();
                                const categoryFields = fields;
                                const allSelected = categoryFields.every(f =>
                                  selectedFields.includes(f)
                                );
                                handleCategoryToggle(
                                  categoryFields,
                                  !allSelected
                                );
                              }}
                              sx={{ fontSize: '0.7rem' }}
                            >
                              {fields.every(f => selectedFields.includes(f))
                                ? 'Deselect'
                                : 'Select'}{' '}
                              All
                            </Button>
                          </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                          <FormGroup>
                            <Grid container spacing={1}>
                              {fields.map(field => (
                                <Grid item xs={12} sm={6} md={4} key={field}>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        checked={selectedFields.includes(field)}
                                        onChange={() =>
                                          handleFieldToggle(field)
                                        }
                                        size='small'
                                      />
                                    }
                                    label={
                                      <Typography
                                        variant='body2'
                                        sx={{ fontSize: '0.8rem' }}
                                      >
                                        {formatFieldName(field)}
                                      </Typography>
                                    }
                                  />
                                </Grid>
                              ))}
                            </Grid>
                          </FormGroup>
                        </AccordionDetails>
                      </Accordion>
                    )
                  )}
                </Box>
              </Box>
            )
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setExportDialog(false)}>Cancel</Button>
          <Button
            variant='contained'
            onClick={handleExportConfirm}
            disabled={
              (exportSource === 'raw' && selectedFields.length === 0) ||
              (exportSource === 'filtered' && !hasFilteredData)
            }
            startIcon={<Download />}
          >
            Export{' '}
            {exportSource === 'raw'
              ? `${selectedFields.length} Fields`
              : 'Data'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ExportButton;
